import clsx from "clsx";
import type { BattleBracketView, GuildBattleCurrentMatch } from "../../api/guild";

interface TournamentRound {
  guild_a_id?: number;
  guild_b_id?: number | null;
  guild_a_name?: string;
  guild_b_name?: string | null;
  winner_guild_id?: number | null;
  winner_guild_name?: string | null;
  status?: string;
}

interface GuildTournamentBracketProps {
  tree: { rounds?: TournamentRound[] } | null;
  bracket: BattleBracketView | null;
  currentMatch?: GuildBattleCurrentMatch | null;
  compact?: boolean;
}

function RosterColumn({
  title,
  entries,
  highlight,
}: {
  title: string;
  entries: { label: string; status: string }[];
  highlight?: string[];
}) {
  const active = entries.filter((e) => e.status === "active");
  const reserve = entries.filter((e) => e.status === "reserve");
  const fallen = entries.filter((e) => e.status === "fallen");

  return (
    <div className="space-y-1">
      <p className="text-[10px] font-bold text-gray-400 uppercase">{title}</p>
      {active.length > 0 && (
        <div className="space-y-0.5">
          <p className="text-[9px] text-emerald-500">On field</p>
          {active.map((e) => (
            <p
              key={e.label}
              className={clsx(
                "text-[10px] truncate",
                highlight?.includes(e.label) ? "text-amber-200 font-semibold" : "text-white",
              )}
            >
              {e.label}
            </p>
          ))}
        </div>
      )}
      {reserve.length > 0 && (
        <div className="space-y-0.5">
          <p className="text-[9px] text-violet-400">Reserve ({reserve.length})</p>
          {reserve.map((e) => (
            <p key={e.label} className="text-[10px] text-gray-400 truncate">
              {e.label}
            </p>
          ))}
        </div>
      )}
      {fallen.length > 0 && (
        <div className="space-y-0.5">
          <p className="text-[9px] text-rose-500/80">Fallen ({fallen.length})</p>
          {fallen.slice(0, compactLimit(fallen.length)).map((e) => (
            <p key={e.label} className="text-[10px] text-gray-600 truncate line-through">
              {e.label}
            </p>
          ))}
        </div>
      )}
    </div>
  );
}

function compactLimit(n: number): number {
  return n > 5 ? 5 : n;
}

function LineWarBracket({
  bracket,
  currentMatch,
  compact,
}: {
  bracket: BattleBracketView;
  currentMatch?: GuildBattleCurrentMatch | null;
  compact?: boolean;
}) {
  const highlightA = currentMatch?.team_a ?? [];
  const highlightB = currentMatch?.team_b ?? [];

  return (
    <div className="space-y-2">
      <div className="flex justify-between items-center">
        <p className="text-xs font-bold text-white">
          {bracket.guild_a_name ?? "?"} vs {bracket.guild_b_name ?? "?"}
        </p>
        {(bracket.round_num ?? 0) > 0 && (
          <span className="text-[10px] text-gray-500">Round {bracket.round_num}</span>
        )}
      </div>
      <div className="grid grid-cols-2 gap-3">
        <RosterColumn
          title={bracket.guild_a_name ?? "Attackers"}
          entries={bracket.roster_a ?? []}
          highlight={highlightA}
        />
        <RosterColumn
          title={bracket.guild_b_name ?? "Defenders"}
          entries={bracket.roster_b ?? []}
          highlight={highlightB}
        />
      </div>
      {currentMatch && !compact && (
        <p className="text-xs text-amber-200 font-semibold animate-pulse text-center">
          Now: {(currentMatch.team_a ?? []).join(", ")} vs {(currentMatch.team_b ?? []).join(", ")}
        </p>
      )}
    </div>
  );
}

function TournamentRounds({
  rounds,
  revealed,
  currentMatch,
}: {
  rounds: TournamentRound[];
  revealed?: number;
  currentMatch?: GuildBattleCurrentMatch | null;
}) {
  const showCount = revealed != null ? Math.max(revealed, rounds.length) : rounds.length;

  return (
    <ul className="space-y-1">
      {rounds.slice(0, showCount).map((r, i) => {
        const isCurrent =
          currentMatch &&
          r.guild_a_id === currentMatch.guild_a_id &&
          r.guild_b_id === currentMatch.guild_b_id &&
          r.status !== "complete";
        const aName = r.guild_a_name ?? `Guild #${r.guild_a_id ?? "?"}`;
        const bName = r.guild_b_name ?? (r.guild_b_id != null ? `Guild #${r.guild_b_id}` : "—");
        const done = r.status === "complete" || r.winner_guild_id != null;
        const winner =
          r.winner_guild_name ??
          (r.winner_guild_id === r.guild_a_id ? aName : r.winner_guild_id === r.guild_b_id ? bName : null);

        return (
          <li
            key={`${r.guild_a_id}-${r.guild_b_id}-${i}`}
            className={clsx(
              "text-[10px] rounded-lg px-2 py-1",
              isCurrent ? "bg-amber-500/10 border border-amber-500/40 text-amber-200" : "text-gray-400",
            )}
          >
            {aName} vs {bName}
            {done && winner && <span className="text-emerald-400"> → {winner}</span>}
            {!done && r.status === "pending" && <span className="text-gray-500"> (upcoming)</span>}
            {!done && r.status === "bye" && <span className="text-violet-300"> (bye)</span>}
          </li>
        );
      })}
    </ul>
  );
}

function PlatinumBracket({ bracket }: { bracket: BattleBracketView }) {
  return (
    <div className="space-y-2 text-[10px]">
      <p className="text-xs font-bold text-violet-200">Seat #{bracket.seat_index ?? "?"}</p>
      {bracket.incumbent_label && (
        <p className="text-gray-400">
          Defending: <span className="text-white">{bracket.incumbent_label}</span>
        </p>
      )}
      {(bracket.contenders?.length ?? 0) > 0 && (
        <div>
          <p className="text-violet-400 mb-0.5">Contenders</p>
          <ul className="space-y-0.5">
            {bracket.contenders!.map((c) => (
              <li key={c} className="text-gray-300 truncate">
                {c}
              </li>
            ))}
          </ul>
        </div>
      )}
      {(bracket.contender_winners?.length ?? 0) > 0 && (
        <div>
          <p className="text-emerald-500 mb-0.5">Advanced</p>
          <ul className="space-y-0.5">
            {bracket.contender_winners!.map((c) => (
              <li key={c} className="text-emerald-300 truncate">
                {c}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export function GuildTournamentBracket({
  tree,
  bracket,
  currentMatch,
  compact,
}: GuildTournamentBracketProps) {
  const tournamentRounds =
    bracket?.mode === "tournament"
      ? bracket.rounds
      : bracket?.mode === "line_war" && (bracket.tournament_rounds?.length ?? 0) > 0
        ? bracket.tournament_rounds
        : tree?.rounds;
  const showTournament = (tournamentRounds?.length ?? 0) > 0;
  const showLineWar = bracket?.mode === "line_war";
  const hasContent =
    showTournament ||
    showLineWar ||
    bracket?.mode === "platinum" ||
    currentMatch;

  if (!hasContent) return null;

  return (
    <section className="border border-kingdom-mid rounded-xl p-3 space-y-2">
      <h4 className="text-xs font-bold text-white">Battle bracket</h4>
      {showTournament && (
        <TournamentRounds
          rounds={tournamentRounds ?? []}
          revealed={bracket?.revealed_rounds}
          currentMatch={currentMatch}
        />
      )}
      {showLineWar && (
        <LineWarBracket bracket={bracket} currentMatch={currentMatch} compact={compact} />
      )}
      {bracket?.mode === "platinum" && <PlatinumBracket bracket={bracket} />}
      {showLineWar && bracket.pending && (
        <p className="text-[10px] text-gray-500 text-center">Line war starting…</p>
      )}
    </section>
  );
}

interface GuildBattleBracketIntroProps {
  bracket: BattleBracketView | null;
  tree: { rounds?: TournamentRound[] } | null;
  phaseLabel?: string | null;
  title?: string;
  onDone: () => void;
}

export function GuildBattleBracketIntro({
  bracket,
  tree,
  phaseLabel,
  title = "⚔️ Battle starting",
  onDone,
}: GuildBattleBracketIntroProps) {
  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/85 p-4">
      <div className="w-full max-w-md space-y-4">
        <div className="text-center space-y-1">
          <p className="text-lg font-bold text-amber-200 font-game">{title}</p>
          {phaseLabel && <p className="text-sm text-gray-400">{phaseLabel}</p>}
        </div>
        <GuildTournamentBracket tree={tree} bracket={bracket} compact={false} />
        <button
          type="button"
          onClick={onDone}
          className="w-full py-2 rounded-xl bg-kingdom-mid text-white text-sm font-bold"
        >
          Continue
        </button>
      </div>
    </div>
  );
}
